import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from 'react-signature-canvas';
import { 
	Box, 
	Button, 
	Typography, 
	IconButton,
	TextField
} from "@mui/material";
import { Clear, Create, TextFields } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const CanvasContainer = styled(Box)(({ theme }) => ({
	border: `2px solid ${theme.palette.grey[300]}`,
	borderRadius: theme.shape.borderRadius,
	backgroundColor: "white",
	position: "relative",
}));

const Canvas = styled('div')({
	width: "100%",
	height: "200px",
});

const Controls = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: theme.spacing(1),
	borderTop: `1px solid ${theme.palette.grey[200]}`,
	backgroundColor: theme.palette.grey[50],
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
	display: "flex",
	gap: theme.spacing(1),
}));

const TextInput = styled(TextField)(({ theme }) => ({
	marginBottom: theme.spacing(2),
}));

const Placeholder = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	color: theme.palette.text.secondary,
	fontStyle: "italic",
	pointerEvents: "none",
	zIndex: 1,
}));

interface SignatureCanvasProps {
	onSave: (signatureData: string) => void;
	onCancel: () => void;
	initialSignature?: string;
	showSaveButton?: boolean;
}

const SignatureCanvasComponent: React.FC<SignatureCanvasProps> = ({ 
	onSave, 
	onCancel, 
	initialSignature,
	showSaveButton = false
}) => {
	const sigPadRef = useRef<SignatureCanvas>(null);
	const canvasContainerRef = useRef<HTMLDivElement>(null);
	const [signText, setSignText] = useState("");
	const [isDrawing, setIsDrawing] = useState(false);
	const [hasSignature, setHasSignature] = useState(false);

	const clearCanvas = () => {
		if (sigPadRef.current) {
			sigPadRef.current.clear();
		}
		setHasSignature(false);
	};

	const generateTextSignature = () => {
		if (signText.trim() && sigPadRef.current) {
			sigPadRef.current.clear();
			const canvas = sigPadRef.current.getCanvas();
			const ctx = canvas.getContext("2d");
			
			if (ctx) {
				// Use a larger font that scales with canvas height
				let fontSize = Math.max(48, Math.round(canvas.height * 0.6));
				ctx.font = `${fontSize}px AlexBrush`;

				// Calculate text position to center it with updated font size
				let textWidth = ctx.measureText(signText).width;
				if (textWidth > canvas.width * 0.95) {
					const scaleFactor = (canvas.width * 0.95) / textWidth;
					fontSize = Math.max(48, Math.floor(fontSize * scaleFactor));
					ctx.font = `${fontSize}px AlexBrush`;
					textWidth = ctx.measureText(signText).width;
				}
				const positionX = Math.round((canvas.width - textWidth) / 2);
				const positionY = Math.round(canvas.height / 2);

				ctx.beginPath();
				ctx.textAlign = 'left';
				ctx.textBaseline = 'middle';
				ctx.fillStyle = 'black';
				ctx.fillText(signText, positionX, positionY);
				ctx.stroke();

				setHasSignature(true);
				setIsDrawing(false);
			}
		}
	};

	const handleSave = () => {
		if (sigPadRef.current && hasSignature) {
			const signatureData = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png");
			onSave(signatureData);
		}
	};

	const handleCanvasBegin = () => {
		setIsDrawing(true);
		setHasSignature(true);
	};

	const handleCanvasEnd = () => {
		setIsDrawing(false);
		if (sigPadRef.current) {
			setHasSignature(!sigPadRef.current.isEmpty());
		}
	};


	const resizeCanvas = () => {
		if (sigPadRef.current && canvasContainerRef.current) {
			const container = canvasContainerRef.current;
			const canvas = sigPadRef.current.getCanvas();
			
			// Set canvas size to match container
			canvas.width = container.offsetWidth;
			canvas.height = container.offsetHeight;
			
			// Clear and redraw if there was content
			if (!sigPadRef.current.isEmpty()) {
				const dataURL = sigPadRef.current.toDataURL();
				sigPadRef.current.clear();
				sigPadRef.current.fromDataURL(dataURL);
			}
		}
	};

	// Resize canvas on mount and window resize
	useEffect(() => {
		const timer = setTimeout(() => {
			resizeCanvas();
		}, 100); // Delay to ensure container is rendered

		window.addEventListener('resize', resizeCanvas);
		
		return () => {
			clearTimeout(timer);
			window.removeEventListener('resize', resizeCanvas);
		};
	}, []);


	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Create Your Signature
			</Typography>
			
			<Typography variant="body2" color="textSecondary" gutterBottom>
				Draw your signature below or type your name to generate one
			</Typography>

			<TextInput
				fullWidth
				variant="outlined"
				label="Type your name to generate signature"
				value={signText}
				onChange={(e) => setSignText(e.target.value)}
				InputProps={{
					endAdornment: (
						<IconButton 
							onClick={generateTextSignature}
							disabled={!signText.trim()}
							color="primary"
						>
							<TextFields />
						</IconButton>
					),
				}}
			/>

			<CanvasContainer>
				{!isDrawing && !hasSignature && (
					<Placeholder>
						Draw your signature here or use the text input above
					</Placeholder>
				)}
				
				<Canvas  ref={canvasContainerRef}>
					<SignatureCanvas
						ref={sigPadRef}
						canvasProps={{
							style: {
								width: "100%",
								height: "100%",
								border: "none",
							}
						}}
						penColor="black"
						backgroundColor="white"
						onBegin={handleCanvasBegin}
						onEnd={handleCanvasEnd}
					/>
				</Canvas>
			</CanvasContainer>

			<Controls>
				<ButtonGroup>
					<Button
						variant="outlined"
						startIcon={<Clear />}
						onClick={clearCanvas}
						disabled={!hasSignature}
					>
						Clear
					</Button>
					<Button
						variant="outlined"
						startIcon={<Create />}
						onClick={generateTextSignature}
						disabled={!signText.trim()}
					>
						Generate from Text
					</Button>
				</ButtonGroup>

				<ButtonGroup>
					<Button
						variant="outlined"
						onClick={onCancel}
					>
						Cancel
					</Button>
					{showSaveButton && (
						<Button
							variant="contained"
							color="primary"
							onClick={handleSave}
							disabled={!hasSignature}
						>
							Save Signature
						</Button>
					)}
				</ButtonGroup>
			</Controls>

			{/* Hidden div to preload the font */}
			<div style={{ font: '5px AlexBrush', color: "white", position: "absolute", left: "-9999px" }}>
				.
			</div>
		</Box>
	);
};

export default SignatureCanvasComponent;
